import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import type { ModelSelection, StagedImport } from '../../lib/api-types';
import { Icon } from '../ui/Icon';
import styles from './Chat.module.css';

const MODELS: Array<{ value: ModelSelection; label: string }> = [
  { value: 'auto', label: 'Default' },
  { value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol' },
  { value: 'gpt-6-astra', label: 'GPT-6 Astra' },
  { value: 'gpt-6-luna', label: 'GPT-6 Luna' },
  { value: 'haiku', label: 'Haiku' },
  { value: 'sonnet', label: 'Sonnet' },
  { value: 'opus', label: 'Opus' },
  { value: 'fable', label: 'Fable' },
];

const IMPORT_ACCEPT = '.csv,.tsv,.xlsx';
const MAX_IMPORT_BYTES = 10 * 1024 * 1024;

function isImportFile(file: File): boolean {
  return /\.(csv|tsv|xlsx)$/i.test(file.name);
}


export function Composer({
  onSend,
  onStop,
  onStageImport,
  isStreaming = false,
  model,
  onModelChange,
  provider = 'anthropic',
  placeholder = 'Message Resonance…',
}: {
  onSend: (text: string) => void;
  onStop: () => void;
  onStageImport?: (file: File) => Promise<StagedImport>;
  isStreaming?: boolean;
  provider?: string;
  model: ModelSelection;
  onModelChange: (model: ModelSelection) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState('');
  const [isMultiline, setIsMultiline] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [upload, setUpload] = useState<{ filename: string; status: 'staging' | 'error'; message?: string } | null>(null);
  const composer = useRef<HTMLDivElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const attachmentButton = useRef<HTMLButtonElement>(null);
  const modelPicker = useRef<HTMLDivElement>(null);
  const voiceButton = useRef<HTMLButtonElement>(null);
  const sendButton = useRef<HTMLButtonElement>(null);

  const measure = useCallback((): void => {
    const element = textarea.current;
    const container = composer.current;
    if (!element || !container) return;

    const containerStyle = window.getComputedStyle(container);
    const inputStyle = window.getComputedStyle(element);
    const gap = Number.parseFloat(containerStyle.columnGap) || 0;
    const horizontalPadding =
      (Number.parseFloat(containerStyle.paddingLeft) || 0) +
      (Number.parseFloat(containerStyle.paddingRight) || 0);
    const controls = [attachmentButton.current, modelPicker.current, voiceButton.current, sendButton.current]
      .filter((control): control is HTMLDivElement | HTMLButtonElement => control !== null);
    const availableInputWidth = Math.max(
      0,
      container.clientWidth -
        horizontalPadding -
        controls.reduce((width, control) => width + control.offsetWidth, 0) -
        gap * controls.length,
    );
    const inputPadding =
      (Number.parseFloat(inputStyle.paddingLeft) || 0) +
      (Number.parseFloat(inputStyle.paddingRight) || 0);
    const availableTextWidth = Math.max(0, availableInputWidth - inputPadding);

    // Measure the hypothetical inline value even while the expanded layout gives
    // the textarea the full row. This prevents the layout from flickering around
    // the exact point where the text first wraps.
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) context.font = inputStyle.font;
    const widestLine = text
      .split('\n')
      .reduce((width, line) => Math.max(width, context?.measureText(line).width ?? 0), 0);
    const shouldExpand = text.includes('\n') || widestLine > availableTextWidth;
    setIsMultiline(shouldExpand);

    // Grow with the content up to the CSS max-height, then scroll.
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [isStreaming, model, text]);

  useLayoutEffect(measure, [measure, isMultiline]);

  useEffect(() => {
    const element = composer.current;
    if (!element) return;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  const submit = (): void => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed);
    setText('');
    setIsMultiline(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter sends; Shift+Enter breaks the line — the convention everywhere else.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const stage = useCallback(async (file: File): Promise<void> => {
    if (!onStageImport || isStreaming) return;
    if (!isImportFile(file)) {
      setUpload({ filename: file.name, status: 'error', message: 'Only CSV, TSV, and Excel files can be imported.' });
      return;
    }
    if (file.size > MAX_IMPORT_BYTES) {
      setUpload({ filename: file.name, status: 'error', message: 'Imports are limited to 10 MB.' });
      return;
    }
    setUpload({ filename: file.name, status: 'staging' });
    try {
      const staged = await onStageImport(file);
      setUpload(null);
      // Hand the agent the artifact id; inspect_import owns the profiling from here.
      const sheets = staged.additional_sheets ?? [];
      const sheetNote = sheets.length > 0
        ? ` It also has ${sheets.length} more sheet${sheets.length === 1 ? '' : 's'}: ${
            sheets.map((sheet) => `“${sheet.sheet ?? 'unnamed'}” (artifact ${sheet.artifact_id}, ${sheet.total_rows} rows)`).join(', ')
          }.`
        : '';
      onSend(
        `I uploaded \`${file.name}\` — staged import artifact \`${staged.artifact_id}\`, ${staged.total_rows} rows.${sheetNote} Help me import it.`,
      );
    } catch (error) {
      setUpload({
        filename: file.name,
        status: 'error',
        message: error instanceof Error ? error.message : 'The upload failed.',
      });
    }
  }, [isStreaming, onSend, onStageImport]);

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) void stage(file);
  };

  const uploadsEnabled = Boolean(onStageImport);

  return (
    <div className={styles.composer}>
      {upload ? (
        <div className={styles.composerNotice} data-status={upload.status}>
          <Icon name="notes" size={15} />
          <span>{upload.filename}</span>
          {upload.status === 'staging' ? <span>· reading…</span> : <span>· {upload.message}</span>}
          {upload.status === 'error' ? (
            <button type="button" onClick={() => setUpload(null)} aria-label="Dismiss">
              <Icon name="x" size={14} />
            </button>
          ) : null}
        </div>
      ) : null}
      <div
        ref={composer}
        className={styles.composerInner}
        data-has-text={Boolean(text.trim()) || isStreaming || undefined}
        data-multiline={isMultiline || undefined}
        data-dragging={isDragging || undefined}
        onDragOver={uploadsEnabled ? (event) => { event.preventDefault(); setIsDragging(true); } : undefined}
        onDragLeave={uploadsEnabled ? () => setIsDragging(false) : undefined}
        onDrop={uploadsEnabled ? onDrop : undefined}
      >
        <input
          ref={fileInput}
          type="file"
          accept={IMPORT_ACCEPT}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void stage(file);
            event.target.value = '';
          }}
        />
        <button
          ref={attachmentButton}
          type="button"
          className={`${styles.composerAction} ${styles.attachment}`}
          disabled={!uploadsEnabled || isStreaming || upload?.status === 'staging'}
          onClick={() => fileInput.current?.click()}
          aria-label="Add files"
          title={uploadsEnabled ? 'Import a CSV, TSV, or Excel file' : 'File imports are not available here'}
        >
          <Icon name="plus" size={25} />
        </button>
        <textarea
          ref={textarea}
          className={styles.input}
          rows={1}
          value={text}
          placeholder={placeholder}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Message"
        />
        <div ref={modelPicker} className={styles.modelPicker}>
          <select
            value={model}
            onChange={(event) => onModelChange(event.target.value as ModelSelection)}
            aria-label="Model"
          >
            {MODELS.filter(option => option.value === 'auto' || option.value.startsWith('gpt-') === (provider === 'openai')).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Icon name="chevron-down" size={16} />
        </div>
        <button
          ref={voiceButton}
          type="button"
          className={`${styles.composerAction} ${styles.voice}`}
          disabled
          aria-label="Voice input"
          title="Voice input is not available yet"
        >
          <Icon name="microphone" size={22} />
        </button>
        {isStreaming || text.trim() ? (
          <button
            ref={sendButton}
            type="button"
            className={styles.send}
            data-action={isStreaming ? 'stop' : 'send'}
            onClick={isStreaming ? onStop : submit}
            aria-label={isStreaming ? 'Stop generating' : 'Send'}
            title={isStreaming ? 'Stop generating (Esc)' : undefined}
          >
            <Icon name={isStreaming ? 'stop.fill' : 'arrow-up'} size={isStreaming ? 15 : 19} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
